/**
 * AuthDialog — multi-method sign-in / sign-up modal.
 *
 * Surfaces BOTH:
 *  1. Existing Emergent-managed Google OAuth (top, primary)
 *  2. NEW email + password sign in / sign up (below an "or" divider)
 *
 * Backed by /api/auth/register and /api/auth/login from auth_email.py.
 * On success, refreshes the AuthContext via checkAuth() so the rest of the app
 * sees the new session immediately — no full reload required.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Lock, User as UserIcon, Loader2, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { API, useAuth } from "@/App";

export default function AuthDialog({ open, onOpenChange, defaultTab = "signin" }) {
  const { login: loginWithGoogle, checkAuth } = useAuth();
  const [tab, setTab] = useState(defaultTab);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [signinForm, setSigninForm] = useState({ email: "", password: "" });
  const [signupForm, setSignupForm] = useState({ name: "", email: "", password: "" });
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/auth/login`,
        { email: signinForm.email.trim(), password: signinForm.password },
        { withCredentials: true }
      );
      await checkAuth({ force: true });
      toast.success("Signed in successfully");
      onOpenChange(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't sign in. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Couldn't sign in.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    // Pick up any pending referral code stashed by /join?ref=...
    let ref = null;
    try {
      ref = sessionStorage.getItem("tbc.pending_ref");
    } catch (_) {
      /* no-op */
    }
    try {
      await axios.post(
        `${API}/auth/register`,
        {
          email: signupForm.email.trim(),
          password: signupForm.password,
          name: signupForm.name.trim(),
          ref,
        },
        { withCredentials: true }
      );
      // Clear the ref once consumed so it can't re-fire
      try {
        sessionStorage.removeItem("tbc.pending_ref");
      } catch (_) {
        /* no-op */
      }
      await checkAuth({ force: true });
      toast.success(ref ? "Welcome! You've earned 50 TBC Points." : "Welcome to The Blk Community!");
      onOpenChange(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't create account.";
      toast.error(typeof msg === "string" ? msg : "Couldn't create account.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail.trim() });
      setForgotSent(true);
    } catch (err) {
      // Endpoint always returns 200 to prevent enumeration; only network errors land here.
      toast.error("Couldn't send reset link. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden" data-testid="auth-dialog">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="font-heading text-2xl text-tbc-navy">
            Welcome to TBC
          </DialogTitle>
          <p className="text-sm text-gray-500 font-body">
            Sign in to support Black-owned businesses or list your own.
          </p>
        </DialogHeader>

        <div className="px-6 pt-2 pb-6">
          {/* Google OAuth — top primary path */}
          <Button
            onClick={() => loginWithGoogle()}
            variant="outline"
            className="w-full rounded-full border-tbc-navy/20 hover:border-tbc-navy h-11"
            data-testid="auth-google-button"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Continue with Google
          </Button>

          {/* Divider */}
          <div className="my-5 flex items-center gap-3 text-xs uppercase tracking-wider text-gray-400">
            <span className="h-px flex-1 bg-gray-200" />
            or
            <span className="h-px flex-1 bg-gray-200" />
          </div>

          {tab === "forgot" ? (
            // Forgot password panel
            <div data-testid="auth-forgot-panel">
              <button
                type="button"
                onClick={() => { setTab("signin"); setForgotSent(false); }}
                className="text-xs text-gray-500 hover:text-tbc-navy inline-flex items-center gap-1 mb-4"
                data-testid="auth-forgot-back"
              >
                <ArrowLeft className="w-3 h-3" /> Back to sign in
              </button>
              {forgotSent ? (
                <div className="bg-tbc-cream/40 border border-tbc-gold/30 rounded-lg p-4 text-sm text-tbc-navy">
                  If <strong>{forgotEmail}</strong> is registered, a reset link is on its way.
                  Check your inbox (and spam folder). If you originally signed in
                  with Google, this same link will let you set a password so you
                  always have a backup way in.
                </div>
              ) : (
                <form onSubmit={handleForgot} className="space-y-4">
                  <p className="text-xs text-gray-600 leading-relaxed" data-testid="auth-forgot-hint">
                    Enter your account email. If you originally signed up with Google,
                    this will let you set a password too — useful when Google sign-in is
                    misbehaving.
                  </p>
                  <div>
                    <Label htmlFor="forgot-email" className="text-xs uppercase tracking-wider text-gray-500">
                      Email
                    </Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="forgot-email"
                        type="email"
                        required
                        autoComplete="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        className="pl-9 h-11"
                        placeholder="you@example.com"
                        data-testid="auth-forgot-email-input"
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full h-11"
                    data-testid="auth-forgot-submit"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
                  </Button>
                </form>
              )}
            </div>
          ) : (
            <Tabs value={tab} onValueChange={setTab} className="w-full">
              <TabsList className="grid grid-cols-2 w-full bg-tbc-cream/40">
                <TabsTrigger value="signin" data-testid="auth-tab-signin">Sign In</TabsTrigger>
                <TabsTrigger value="signup" data-testid="auth-tab-signup">Sign Up</TabsTrigger>
              </TabsList>

              {/* Sign In */}
              <TabsContent value="signin" className="mt-5">
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div>
                    <Label htmlFor="signin-email" className="text-xs uppercase tracking-wider text-gray-500">
                      Email
                    </Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="signin-email"
                        type="email"
                        required
                        autoComplete="email"
                        value={signinForm.email}
                        onChange={(e) => setSigninForm({ ...signinForm, email: e.target.value })}
                        className="pl-9 h-11"
                        placeholder="you@example.com"
                        data-testid="auth-signin-email"
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center">
                      <Label htmlFor="signin-password" className="text-xs uppercase tracking-wider text-gray-500">
                        Password
                      </Label>
                      <button
                        type="button"
                        onClick={() => setTab("forgot")}
                        className="text-xs text-tbc-navy hover:text-tbc-gold"
                        data-testid="auth-forgot-link"
                      >
                        Forgot?
                      </button>
                    </div>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="signin-password"
                        type={showPassword ? "text" : "password"}
                        required
                        autoComplete="current-password"
                        value={signinForm.password}
                        onChange={(e) => setSigninForm({ ...signinForm, password: e.target.value })}
                        className="pl-9 pr-9 h-11"
                        placeholder="••••••••"
                        data-testid="auth-signin-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-tbc-navy"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full h-11"
                    data-testid="auth-signin-submit"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
                  </Button>
                </form>
              </TabsContent>

              {/* Sign Up */}
              <TabsContent value="signup" className="mt-5">
                <form onSubmit={handleSignUp} className="space-y-4">
                  <div>
                    <Label htmlFor="signup-name" className="text-xs uppercase tracking-wider text-gray-500">
                      Full name
                    </Label>
                    <div className="relative mt-1">
                      <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="signup-name"
                        type="text"
                        required
                        autoComplete="name"
                        value={signupForm.name}
                        onChange={(e) => setSignupForm({ ...signupForm, name: e.target.value })}
                        className="pl-9 h-11"
                        placeholder="Your name"
                        data-testid="auth-signup-name"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="signup-email" className="text-xs uppercase tracking-wider text-gray-500">
                      Email
                    </Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="signup-email"
                        type="email"
                        required
                        autoComplete="email"
                        value={signupForm.email}
                        onChange={(e) => setSignupForm({ ...signupForm, email: e.target.value })}
                        className="pl-9 h-11"
                        placeholder="you@example.com"
                        data-testid="auth-signup-email"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="signup-password" className="text-xs uppercase tracking-wider text-gray-500">
                      Password
                    </Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="signup-password"
                        type={showPassword ? "text" : "password"}
                        required
                        minLength={8}
                        autoComplete="new-password"
                        value={signupForm.password}
                        onChange={(e) => setSignupForm({ ...signupForm, password: e.target.value })}
                        className="pl-9 pr-9 h-11"
                        placeholder="At least 8 characters"
                        data-testid="auth-signup-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-tbc-navy"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy font-semibold rounded-full h-11"
                    data-testid="auth-signup-submit"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Account"}
                  </Button>
                  <p className="text-[11px] text-gray-400 text-center">
                    By signing up you agree to our community guidelines.
                  </p>
                </form>
              </TabsContent>
            </Tabs>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
